import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { getIdToken } from 'firebase/auth';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, redirect } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { toast } from 'sonner';

import './i18n';

import { Unauthorized } from '@virtual-care/client';
import { MUST_CHANGE_PASSWORD_CLAIM } from '@virtual-care/isomorphic';
import { client as newClient } from '@virtual-care/hooks';
import { Toaster } from '@virtual-care/shadcn';
import { errorToFormIssue, client as legacyClient } from '@virtual-care/ui';

import { auth, getIdentity } from './app/firebase.tsx';
import Layout from './app/layout.tsx';
import ChangePassword from './app/routes/change-password.tsx';
import AppointmentsPage from './app/routes/appointments/list-appointments.tsx';
import B2BAppointmentsPage from './app/routes/appointments/list-b2b-appointments.tsx';
import B2BSecondOpinionPage from './app/routes/appointments/list-b2b-second-opinion.tsx';
import HomeCareAppointmentsPage from './app/routes/appointments/list-homecare-appointments.tsx';
import PrintingRequestsPage from './app/routes/appointments/list-printing-requests.tsx';
import SecondOpinionPage from './app/routes/appointments/list-second-opinion.tsx';
import CoordinatorsPage from './app/routes/coordinators/list-coordinators';
import DoctorsPage from './app/routes/doctors/list-doctors';
import Home from './app/routes/home.tsx';
import HomeCareClinicsPage from './app/routes/homecare/list-homecare-clinics.tsx';
import HomeCareIndividualsPage from './app/routes/homecare/list-homecare-individuals.tsx';
import BtoBPatientsPage from './app/routes/patients/b2b-patients.tsx';
import PatientsPage from './app/routes/patients/list-patients';
import HomeCarePaymentsPage from './app/routes/payments/homecare.tsx';
import SecondOpinionPaymentsPage from './app/routes/payments/second-opinion.tsx';
import StandardPaymentsPage from './app/routes/payments/standard.tsx';
import SettingsAdminPage from './app/routes/settings/settings-admin.tsx';
import SettingsPage from './app/routes/settings/settings.tsx';
import SignIn from './app/routes/signin.tsx';
import PromoCodesPage from './app/routes/promo-codes/list-promo-codes.tsx';
import SpecialitiesPage from './app/routes/specialities/list-specialities.tsx';

async function requiresAuth() {
  const identity = await getIdentity();
  if (!identity) {
    throw redirect('/signin');
  }

  const user = auth.currentUser;
  if (user) {
    const tokenResult = await user.getIdTokenResult();
    const mustChange = Boolean(
      tokenResult.claims[MUST_CHANGE_PASSWORD_CLAIM],
    );
    if (mustChange && window.location.pathname !== '/change-password') {
      throw redirect('/change-password');
    }
  }

  return identity;
}

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error) => {
      console.error(error);
      if (error instanceof Unauthorized) {
        if (error.status === 401) {
          toast.error('You are not authorized to perform this action');
          if (!import.meta.env.DEV) {
            window.location.href = '/';
          }
        }
      } else if (import.meta.env.DEV) {
        const formIssue = errorToFormIssue(error);
        for (const propName in formIssue) {
          if (import.meta.env.DEV) {
            toast.error(
              `Error in propName "${propName}": ${formIssue[propName].map((issue) => issue.message).join(', ')}`,
              { description: (error as any).error },
            );
          }
        }
      }
    },
  }),
  queryCache: new QueryCache({
    onError: (error) => {
      console.error(error);
      const formIssue = errorToFormIssue(error);
      for (const propName in formIssue) {
        if (import.meta.env.DEV) {
          toast.error(
            `Error in propName "${propName}": ${formIssue[propName].map((issue) => issue.message).join(', ')}`,
          );
        }
      }
    },
  }),
});

const router = createBrowserRouter([
  {
    path: '/signin',
    Component: SignIn,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        return null;
      }
      throw redirect('/');
    },
  },
  {
    path: '/change-password',
    Component: ChangePassword,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        throw redirect('/signin');
      }
      return identity;
    },
  },
  {
    Component: Layout,
    loader: requiresAuth,
    children: [
      {
        path: '/',
        index: true,
        Component: Home,
      },
      {
        path: '/doctors',
        Component: DoctorsPage,
      },
      {
        path: '/coordinators',
        Component: CoordinatorsPage,
      },
      {
        path: '/specialities',
        Component: SpecialitiesPage,
      },
      {
        path: '/promo-codes',
        Component: PromoCodesPage,
      },
      {
        path: '/patients',
        Component: PatientsPage,
      },
      {
        path: '/patients/b2b',
        Component: BtoBPatientsPage,
      },
      {
        path: '/appointments',
        children: [
          {
            path: 'standard',
            Component: AppointmentsPage,
          },
          {
            path: 'second-opinion',
            Component: SecondOpinionPage,
          },
          {
            path: 'homecare',
            Component: HomeCareAppointmentsPage,
          },
          {
            path: 'b2b-standard',
            Component: B2BAppointmentsPage,
          },
          {
            path: 'b2b-second-opinion',
            Component: B2BSecondOpinionPage,
          },
          {
            path: 'printing-requests',
            Component: PrintingRequestsPage,
          },
        ],
      },
      {
        path: '/homecare/clinic',
        Component: HomeCareClinicsPage,
      },
      {
        path: '/homecare/individual',
        Component: HomeCareIndividualsPage,
      },
      {
        path: '/payments',
        children: [
          {
            path: 'standard',
            Component: StandardPaymentsPage,
          },
          {
            path: 'second-opinion',
            Component: SecondOpinionPaymentsPage,
          },
          {
            path: 'homecare',
            Component: HomeCarePaymentsPage,
          },
        ],
      },
      {
        path: '/settings',
        Component: SettingsPage,
      },
      {
        // this page is hidden from the sidebar
        path: '/settings-admin',
        Component: SettingsAdminPage,
      },
    ],
  },
]);

// Set the token for API client
legacyClient.setOptions({
  baseUrl: import.meta.env.VITE_API_URL,
  token: () => {
    if (auth.currentUser) {
      return getIdToken(auth.currentUser);
    }
    return '';
  },
});
newClient.setOptions({
  baseUrl: import.meta.env.VITE_API_URL,
  token: () => {
    if (auth.currentUser) {
      return getIdToken(auth.currentUser);
    }
    return '';
  },
});

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <Toaster />
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
